import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// ==================== ICONS ====================
const UserIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
  </svg>
);

const EnvelopeIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
  </svg>
);

const ShieldCheckIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
  </svg>
);

const BuildingOffice2Icon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z" />
  </svg>
);

const HandRaisedIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.05 4.575a1.575 1.575 0 10-3.15 0v3m3.15-3v-1.5a1.575 1.575 0 013.15 0v1.5m-3.15 0l.075 5.925m3.075.75V4.575m0 0a1.575 1.575 0 013.15 0V15M6.9 7.575a1.575 1.575 0 10-3.15 0v8.175a6.75 6.75 0 006.75 6.75h2.018a5.25 5.25 0 003.712-1.538l1.732-1.732a5.25 5.25 0 001.538-3.712l.003-2.024a.668.668 0 01.198-.471 1.575 1.575 0 10-2.228-2.228 3.818 3.818 0 00-1.12 2.687M6.9 7.575V12m6.27 4.318A4.49 4.49 0 0116.35 15m.002 0h-.002" />
  </svg>
);

// ==================== REUSABLE COMPONENTS ====================

const Container = ({ className = "", children }) => (
  <div className={`mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 ${className}`}>{children}</div>
);

const SectionHeader = ({ eyebrow, title, description, align = "left" }) => (
  <div className={align === "center" ? "text-center max-w-3xl mx-auto" : "max-w-3xl"}>
    {eyebrow && (
      <div className={align === "center" ? "flex justify-center" : ""}>
        <span className="inline-flex items-center gap-2 text-school-goldDark font-semibold tracking-[0.18em] uppercase text-xs">
          <span className="h-1.5 w-1.5 rounded-full bg-school-gold" aria-hidden="true" />
          {eyebrow}
        </span>
      </div>
    )}
    <h2 className="mt-3 text-3xl sm:text-4xl md:text-5xl font-heading font-bold text-school-blue leading-[1.08] tracking-tight">
      {title}
    </h2>
    {description && (
      <p className={`mt-4 text-base md:text-lg text-gray-600 leading-7 ${align === "center" ? "mx-auto" : ""}`}>
        {description}
      </p>
    )}
  </div>
);

const StaffCard = ({ name, role, emailUrl, imageUrl }) => (
  <div className="flex flex-col items-center p-8 bg-white rounded-3xl border border-gray-100 shadow-soft hover:shadow-md transition-all duration-300">
    <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-100 border-4 border-school-gold/20 shadow-sm mb-6">
      {imageUrl ? (
        <img src={imageUrl} alt={name} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-school-blue/8 text-school-blue">
          <UserIcon className="w-12 h-12" />
        </div>
      )}
    </div>
    <h3 className="text-xl md:text-2xl font-heading font-bold text-gray-900 mb-2 tracking-tight text-center">
      {name}
    </h3>
    <p className="text-base text-school-goldDark font-medium mb-4">{role}</p>
    <a
      href={emailUrl}
      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-school-blue/8 text-school-blue text-sm font-semibold hover:bg-school-blue hover:text-white transition-colors"
    >
      <EnvelopeIcon />
      Send Email
    </a>
  </div>
);

const FeatureCard = ({ icon, title, description }) => (
  <div className="flex flex-col items-center text-center p-8 bg-white rounded-3xl border border-gray-100 shadow-soft hover:shadow-md transition-all duration-300 hover:-translate-y-0.5">
    <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-school-blue/8 text-school-blue mb-6 shadow-sm">
      {icon}
    </div>
    <h3 className="text-xl md:text-2xl font-heading font-bold text-gray-900 mb-3 tracking-tight leading-tight">
      {title}
    </h3>
    <p className="text-sm md:text-base text-gray-600 leading-7 max-w-sm mx-auto">{description}</p>
  </div>
);

// ==================== MAIN COMPONENT ====================

const SchoolAides = () => {
  const staff = [
    {
      name: "Binta Bah",
      role: "School Aid",
      emailUrl: "/coming-soon",
      imageUrl: null
    },
    {
      name: "Keana Casey",
      role: "School Aid",
      emailUrl: "/coming-soon",
      imageUrl: null
    },
    {
      name: "Asaiah Garcia",
      role: "School Aid",
      emailUrl: "/coming-soon",
      imageUrl: null
    },
    {
      name: "Pedro Garcia",
      role: "School Aid",
      emailUrl: "/coming-soon",
      imageUrl: null
    },
    {
      name: "Rosalind Grave-Wilson",
      role: "School Aid",
      emailUrl: "/coming-soon",
      imageUrl: null
    },
    {
      name: "Arianna Jimenez",
      role: "School Aid",
      emailUrl: "/coming-soon",
      imageUrl: null
    },
    {
      name: "Reese Marshall",
      role: "School Aid",
      emailUrl: "/coming-soon",
      imageUrl: null
    },
    {
      name: "Leidy Martinez",
      role: "School Aid",
      emailUrl: "/coming-soon",
      imageUrl: null
    },
    {
      name: "Aneudy Mendoza",
      role: "School Aid",
      emailUrl: "/coming-soon",
      imageUrl: null
    },
    {
      name: "Addrian Mercerdes-Guerra",
      role: "School Aid",
      emailUrl: "/coming-soon",
      imageUrl: null
    },
    {
      name: "Helen Nevarez",
      role: "School Aid",
      emailUrl: "/coming-soon",
      imageUrl: null
    },
    {
      name: "Cynthia Reyes",
      role: "School Aid",
      emailUrl: "/coming-soon",
      imageUrl: null
    },
    {
      name: "Maria Rivera",
      role: "School Aid",
      emailUrl: "/coming-soon",
      imageUrl: null
    },
    {
      name: "Kelvin Rodriguez",
      role: "School Aid",
      emailUrl: "/coming-soon",
      imageUrl: null
    },
    {
      name: "Miguel Rodriguez",
      role: "School Aid",
      emailUrl: "/coming-soon",
      imageUrl: null
    },
    {
      name: "Vivian Rodriguez",
      role: "School Aid",
      emailUrl: "/coming-soon",
      imageUrl: null
    },
    {
      name: "Yojandy Soriano",
      role: "School Aid",
      emailUrl: "/coming-soon",
      imageUrl: null
    },
    {
      name: "Chris Wint",
      role: "School Aid",
      emailUrl: "/coming-soon",
      imageUrl: null
    }
  ];

  return (
    <div className="min-h-screen font-sans text-gray-600 antialiased selection:bg-school-gold/30 selection:text-school-blue" style={{ backgroundColor: '#F5EFE0' }}>
      <Navbar />

      <main id="main-content">

        {/* ==================== HERO SECTION ==================== */}
        <section className="relative min-h-[50vh] md:min-h-[60vh] flex items-center overflow-hidden bg-[#0F2240]">
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.35),transparent_55%)]" aria-hidden="true" />
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 100%, rgba(197,145,50,0.1) 0%, transparent 70%)' }} />

          <Container className="relative z-10 pt-32 pb-16 md:pt-40 md:pb-20">
            <div className="max-w-4xl bg-[#0F2240]">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white/90 text-xs sm:text-sm font-semibold mb-7">
                <span className="w-2 h-2 rounded-full bg-school-gold" aria-hidden="true" />
                Contact Us
              </div>

              <h1 className="text-4xl sm:text-5xl md:text-6xl font-heading font-bold text-white tracking-tight leading-[1.05]">
                School Aides
              </h1>

              <p className="mt-6 text-lg md:text-xl text-gray-200 leading-relaxed max-w-3xl">
                Maintaining a safe, clean, and welcoming learning environment
              </p>
            </div>
          </Container>
        </section>

        {/* ==================== INTRODUCTION ==================== */}
        <section className="py-20 md:py-28 bg-[#F5EFE0]">
          <Container>
            <SectionHeader
              align="center"
              eyebrow="Essential Support"
              title="Dedicated to Our School Community"
              description="Our school aides and security staff work tirelessly to create a safe, clean, and organized environment where students can thrive"
            />
          </Container>
        </section>

        {/* ==================== SERVICES ==================== */}
        <section className="py-20 md:py-28 bg-[#EDE5D0]">
          <Container>
            <SectionHeader
              align="center"
              eyebrow="What We Do"
              title="Supporting Daily Operations"
              description="Our team ensures a safe and well-maintained school environment"
            />

            <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              <FeatureCard
                icon={<ShieldCheckIcon />}
                title="School Safety"
                description="Security guards monitoring entrances and maintaining a secure campus for students and staff."
              />

              <FeatureCard
                icon={<BuildingOffice2Icon />}
                title="Facility Maintenance"
                description="Keeping classrooms, hallways, and common areas clean and well-organized."
              />

              <FeatureCard
                icon={<HandRaisedIcon />}
                title="Student Support"
                description="Assisting with daily operations and providing support throughout the school day."
              />
            </div>
          </Container>
        </section>

        {/* ==================== STAFF SECTION ==================== */}
        <section className="py-20 md:py-28 bg-white">
          <Container>
            <SectionHeader
              align="center"
              eyebrow="Meet the Team"
              title="Our School Aides & Security Staff"
              description="Committed to maintaining a safe and welcoming school environment"
            />

            <figure className="mt-14 mx-auto max-w-5xl">
              <img
                src="/assets/images/staff-teams/school-aides.jpg"
                width={1600}
                height={700}
                alt="Members of the School Aides team standing together in front of a Welcome to Great bulletin board decorated with paper flowers."
                loading="lazy"
                className="w-full h-auto rounded-3xl shadow-xl shadow-school-blue/10"
              />
            </figure>

            <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {staff.map((member, index) => (
                <StaffCard key={index} {...member} />
              ))}
            </div>
          </Container>
        </section>

        {/* ==================== CTA SECTION ==================== */}
        <section className="relative py-24 md:py-32 bg-school-gold overflow-hidden">
          <div className="absolute -right-20 -top-20 w-96 h-96 bg-school-blue/8 rounded-full blur-3xl pointer-events-none" />

          <Container className="relative text-center">
            <h2 className="text-4xl md:text-6xl font-heading font-bold text-school-blue mb-6">
              Thank You to Our Team
            </h2>
            <p className="text-xl text-school-blue max-w-2xl mx-auto mb-10 font-light">
              We appreciate the hard work and dedication of our school aides and security staff.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/coming-soon"
                className="px-8 py-4 rounded-full bg-school-blue text-white font-bold hover:bg-[#0F2240] transition-colors shadow-soft"
              >
                Contact Us
              </a>
              <a
                href="/"
                className="px-8 py-4 rounded-full bg-white/30 text-school-blue border-2 border-school-blue/20 font-medium hover:bg-white/50 transition-colors"
              >
                Back to Home
              </a>
            </div>
          </Container>
        </section>

        <Footer />
      </main>
    </div>
  );
};

export default SchoolAides;
